<template>
  <template v-if="isLoading">
    <p>Loading...</p>
  </template>
  <template v-else>
    <div v-if="!readonly" class="row">
      <template v-for="field in fields">
        <div v-if="field.type === 'select-option' && field.show" :key="field.id" :class="field.fullColumn ? 'col-sm-12 mb-3 ps-4' : 'col-sm-6 mb-3'">
          <label :for="field.id + 'select-option'" class="form-label"
            >{{ field.label }}
            <a v-if="Boolean(field.image)" @click="$emit('infoButtonClicked', field.image)"
              ><el-icon :size="15" style="position: relative; left: 2px; top: 3px"><InfoFilled /></el-icon></a
          ></label>
          <select
            :id="field.id + 'select-option'"
            v-model="field.value"
            class="form-select"
            @change="(e: Event) => handleChange((e?.target as HTMLSelectElement)?.value, field)"
          >
            <option v-for="option in field.options" :key="option.id" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
        <div v-if="field.type === 'input-text' && field.show" :key="field.id" :class="field.fullColumn ? 'col-sm-12 mb-3 ps-4' : 'col-sm-6 mb-3'">
          <label :for="field.id + 'input-text'" class="form-label"
            >{{ field.label }}
            <a v-if="Boolean(field.image)" @click="$emit('infoButtonClicked', field.image)"
              ><el-icon :size="15" style="position: relative; left: 2px; top: 3px"><InfoFilled /></el-icon></a
          ></label>
          <input
            :id="field.id + 'input-text'"
            v-model="field.value"
            type="text"
            class="form-control"
            @change="
              (e: Event) => {
                handleChange((e.target as HTMLInputElement)?.value as any, field);
              }
            "
          />
        </div>
        <div v-if="field.type === 'switch' && field.show" :key="field.id" :class="field.fullColumn ? 'col-sm-12 mb-3 ps-4' : 'col-sm-6 mb-3'">
          <label :for="field.id + 'switch'" class="form-label"
            >{{ field.label }}
            <a v-if="Boolean(field.image)" @click="$emit('infoButtonClicked', field.image)"
              ><el-icon :size="15" style="position: relative; left: 2px; top: 3px"><InfoFilled /></el-icon></a
          ></label>
          <br />
          <el-switch :id="field.id + 'switch'" v-model="field.value" @change="(value: any) => handleChange(value, field)" />
        </div>
        <div v-if="field.type === 'slider' && field.show" :key="field.id" :class="field.fullColumn ? 'col-sm-12 mb-3 ps-4' : 'col-sm-6 mb-3'">
          <label :for="field.id + 'slider'" class="form-label"
            >{{ field.label }}
            <a v-if="Boolean(field.image)" @click="$emit('infoButtonClicked', field.image)"
              ><el-icon :size="15" style="position: relative; left: 2px; top: 3px"><InfoFilled /></el-icon></a
          ></label>
          <el-slider :id="field.id + 'slider'" v-model="field.value" :min="field.min" :max="field.max" @change="(value: any) => handleChange(value, field)" />
          <el-input-number v-model="field.value" />
        </div>
        <div v-if="field.type === 'radio-button' && field.show" :key="field.id" :class="field.fullColumn ? 'col-sm-12 mb-3 ps-4' : 'col-sm-6 mb-3'">
          <label :for="field.id + 'radio-button'" class="form-label"
            >{{ field.label }}
            <a v-if="Boolean(field.image)" @click="$emit('infoButtonClicked', field.image)"
              ><el-icon :size="15" style="position: relative; left: 2px; top: 3px"><InfoFilled /></el-icon></a></label
          ><br />
          <el-radio-group :id="field.id + 'radio-button'" v-model="field.value" size="large">
            <el-radio-button v-for="opt in field.options" :key="opt.id" :label="opt.value" @change="() => handleChange(opt.value, field)">
              <template v-if="opt.image">
                {{ opt.label }} <br />
                <img class="parameter-img" :src="opt.image" />
              </template>
              <template v-else>
                {{ opt.label }}
              </template>
            </el-radio-button>
          </el-radio-group>
        </div>
      </template>
    </div>
    <div v-else class="row">
      <h3 class="mt-3">{{ $t('components.order-summary.parameterTitle') }}</h3>
      <div v-for="field in fields" :key="field.id" class="col-12 mb-2">
        <div class="row1">
          <label class="col-sm-6 form-label text-secondary mb-1" for="body-side-readonly">{{ field.label }}</label>
          <label class="col-sm-6 form-label mb-1">{{ field.value }}</label>
        </div>
      </div>
    </div>
  </template>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { cloneDeep } from '@apollo/client/utilities';

import { BodySide } from '@/types/enum/order';
import * as CustomerService from '@/services/api/customerService';
import Auth from '@/services/api/auth';
import { Body_Attribute } from '@/types/api/Patient/BodyAttribute';
import { Product } from '@/types/api/Model/Product';
import * as FormBuilderService from '@/services/api/formBuilderService';

@Component({
  components: {},
  emits: ['updateProperties', 'infoButtonClicked'],
})
/* eslint-disable @typescript-eslint/no-explicit-any*/
@Component
export default class ProductParamterTemplate extends Vue {
  @Prop() properties!: any[];
  @Prop() product!: Product;
  @Prop({ default: false }) readonly!: boolean;

  authentication = Auth.getInstance();
  bodyAttributes: Body_Attribute[] = [];

  BodySide = BodySide;
  selectedBodyAttribute: Body_Attribute | null = null;
  fields: any[] = [];
  isLoading = false;

  mounted(): void {
    if (this.authentication.user) {
      CustomerService.getBodyAttributesByUserId(this.authentication.user.userId).then((bodyAttributes) => {
        this.bodyAttributes = bodyAttributes;
        this.getFormBuilderConfigs();
      });
    }
  }

  getFormBuilderConfigs() {
    this.isLoading = true;
    FormBuilderService.getProductFormBuilder(this.product.productId)
      .then((res) => {
        const parsedFields = JSON.parse(res || '[]');
        const newFields = parsedFields.map((cField: { key: string; value: any; }) => {
          const show = this.checkCondition(cField, parsedFields);
          Object.entries(this.properties[0]).forEach(([key, value]) => {
            if (key === cField.key) cField.value = value || cField.value;
          });
          return {
            ...cField,
            show,
          };
        });
        this.fields = newFields;
        this.isLoading = false;
      })
      .catch((err) => {
        console.error('err', err);
        this.isLoading = false;
      });
  }

  checkCondition(cField: any, fields?: any) {
    const fs = fields ? fields : this.fields;
    let shouldShow = true;
    if (cField.conditions) {
      cField.conditions.forEach((cs: { idRelation: any; value: { toString: () => any; }; }) => {
        const relatedField = fs.find((f: { id: any; }) => f.id === cs.idRelation);
        if (relatedField) shouldShow = relatedField.value.toString() === cs.value.toString();
      });
    }
    return shouldShow;
  }

  handleChange(value: any, field: { id: number }) {
    try {
      const clonedFields = cloneDeep(this.fields);
      const newFields = clonedFields.map((cField) => {
        const newField = cloneDeep(cField);
        if (cField.id === field.id) newField.value = value;
        const show = this.checkCondition(cField);
        return {
          ...newField,
          show,
        };
      });
      this.fields = newFields;
      const productProperties: Record<string, any> = {};
      newFields.forEach((f) => {
        productProperties[f.key] = f.value;
      });
      this.$emit('updateProperties', productProperties);
    } catch (error) {
      console.error(error);
    }
  }
}
</script>

<style scoped lang="scss">
.text-secondary {
  color: #0000008d !important;
  text-align: left !important;
}

.row1 {
  border-bottom: 0.1rem solid #6767678d;
}

.form-label {
  font-size: 1rem;
  margin: 0.7rem 0 0.5rem 0;
  text-align: right;
}

.parameter-img {
  padding-top: 12px;
  width: 100px;
  height: 100px;
}
</style>
